import { useState, type FormEvent } from "react";

import { api, clinicHeader, readError } from "../api/client";
import { shapesFor, valuesFrom, type RecordShape } from "../recordFields";
import type { RecordType } from "../types";
import { ErrorNote } from "./ErrorNote";

type Mode =
  | { kind: "create"; patientId: string }
  | { kind: "correct"; recordId: string; type: RecordType; payload: Record<string, unknown> };

/**
 * Write a record, or correct one.
 *
 * One form for both, because a correction is a new record rather than an edit
 * of the old one (CLN-F-014, and safety rule 3 in CLAUDE.md). The fields are
 * the same fields, and pre-filling them from the original is the only
 * difference a doctor sees.
 *
 * A new record is held back from the patient by default, and the form says so
 * rather than offering a switch. Release is a separate decision a doctor makes
 * once they have read what they wrote (CLN-F-021).
 */
export function RecordForm({
  mode,
  role,
  onDone,
  onCancel,
}: {
  mode: Mode;
  role: string | undefined;
  onDone: () => void;
  onCancel: () => void;
}) {
  const offered = shapesFor(role);
  const startingShape =
    mode.kind === "correct"
      ? offered.find((shape) => shape.type === mode.type)
      : offered[0];

  const [shape, setShape] = useState<RecordShape | undefined>(startingShape);
  const [values, setValues] = useState<Record<string, string>>(
    mode.kind === "correct" && startingShape ? valuesFrom(mode.payload, startingShape) : {},
  );
  const [restricted, setRestricted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!shape) {
    return (
      <p className="empty">Your role at this clinic does not write records.</p>
    );
  }

  function set(name: string, value: string) {
    setValues((previous) => ({ ...previous, [name]: value }));
  }

  function payload() {
    // An empty box means the doctor left it out, which is not the same as
    // clearing it to an empty string. The server's allow-list drops anything
    // it does not recognise either way.
    const out: Record<string, string> = {};
    for (const [name, value] of Object.entries(values)) {
      if (value.trim() !== "") out[name] = value.trim();
    }
    return out;
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!shape) return;
    setBusy(true);
    setError(null);

    const result =
      mode.kind === "create"
        ? await api.POST("/portal/patients/{id}/records", {
            params: { path: { id: mode.patientId }, header: clinicHeader() },
            body: {
              type: shape.type,
              payload: payload(),
              ...(restricted ? { sensitivity_level: "restricted" as const } : {}),
            },
          })
        : await api.POST("/portal/records/{id}/supersede", {
            params: { path: { id: mode.recordId }, header: clinicHeader() },
            body: { type: shape.type, payload: payload() },
          });

    setBusy(false);
    if (result.error) {
      setError(readError(result.error).message);
      return;
    }
    onDone();
  }

  return (
    <form className="record-form" onSubmit={submit}>
      <h3>
        {mode.kind === "create" ? "Add to the chart" : "Correct this record"}
      </h3>

      {mode.kind === "correct" && (
        <p className="lead">
          The original stays in the chart, marked as replaced. Nothing is edited and nothing is
          deleted, which is what makes the chart hold up as a record.
        </p>
      )}

      {mode.kind === "create" && offered.length > 1 && (
        <>
          <label htmlFor="record-type">What are you recording?</label>
          <select
            id="record-type"
            value={shape.type}
            onChange={(event) => {
              const next = offered.find((s) => s.type === event.target.value);
              setShape(next);
              setValues({});
            }}
          >
            {offered.map((option) => (
              <option key={option.type} value={option.type}>
                {option.label}
              </option>
            ))}
          </select>
        </>
      )}

      {shape.fields.map((field) => (
        <div key={field.name}>
          <label htmlFor={`field-${field.name}`}>
            {field.label}
            {!field.required && <span className="optional"> optional</span>}
          </label>

          {field.kind === "choice" ? (
            <select
              id={`field-${field.name}`}
              value={values[field.name] ?? field.choices?.[0]?.value ?? ""}
              onChange={(event) => set(field.name, event.target.value)}
            >
              {field.choices?.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              id={`field-${field.name}`}
              type={
                field.kind === "number"
                  ? "number"
                  : field.kind === "date"
                    ? "date"
                    : field.kind === "datetime"
                      ? "datetime-local"
                      : "text"
              }
              step={field.kind === "number" ? "any" : undefined}
              value={values[field.name] ?? ""}
              onChange={(event) => set(field.name, event.target.value)}
              required={field.required}
            />
          )}

          {field.hint && <p className="hint">{field.hint}</p>}
        </div>
      ))}

      {mode.kind === "create" && role === "doctor" && (
        <label className="checkbox">
          <input
            type="checkbox"
            checked={restricted}
            onChange={(event) => setRestricted(event.target.checked)}
          />
          Restrict this from other staff at this clinic
        </label>
      )}

      <p className="muted">
        {mode.kind === "create"
          ? "The patient will not see this until you release it."
          : "The correction inherits whatever the original was set to."}
      </p>

      <ErrorNote message={error} />

      <div className="actions">
        <button type="submit" disabled={busy}>
          {busy ? "Saving…" : mode.kind === "create" ? "Add to chart" : "Save the correction"}
        </button>
        <button type="button" className="link" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
